/**
 * ColorGradesManager Component
 *
 * Allows gym owners to manage color grades at their gym.
 * Supports adding, editing, and removing color grades with grade associations.
 */

import { useState, useEffect, type CSSProperties, type FormEvent } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import type { ColorGrade, VGrade, GradeAssociationType } from '@flash/shared';
import { getGradeIndex } from '@flash/shared';
import { ColorPicker } from '../common/ColorPicker';
import { GradeAssociationSelector } from './GradeAssociationSelector';

/**
 * Sort color grades by their associated grade (exact or range min)
 * Colors with 'none' association go to the end, sorted alphabetically
 */
const sortColorGradesByGrade = (colors: ColorGrade[]): ColorGrade[] => {
  return [...colors].sort((a, b) => {
    // Get the grade to sort by (exactGrade or minGrade)
    const getGrade = (c: ColorGrade): VGrade | null => {
      if (c.associationType === 'exact' && c.exactGrade) return c.exactGrade;
      if (c.associationType === 'range' && c.minGrade) return c.minGrade;
      return null;
    };

    const gradeA = getGrade(a);
    const gradeB = getGrade(b);

    // Both have grades - sort by grade index
    if (gradeA && gradeB) {
      return getGradeIndex(gradeA) - getGradeIndex(gradeB);
    }

    // Only one has a grade - put the one with grade first
    if (gradeA && !gradeB) return -1;
    if (!gradeA && gradeB) return 1;

    // Neither has a grade - sort alphabetically by displayName
    return a.displayName.localeCompare(b.displayName);
  });
};

/**
 * Color rename mapping (oldName -> newName)
 */
export type ColorRenameMap = Map<string, string>;

/**
 * Props for ColorGradesManager component
 */
interface ColorGradesManagerProps {
  /** Current list of color grades */
  colorGrades: ColorGrade[];
  /** Callback when grades are updated. Renames map contains oldName -> newName for renamed colors. */
  onSave: (grades: ColorGrade[], renames?: ColorRenameMap) => Promise<void>;
  /** Gym ID for checking routes usage */
  gymId: string;
  /** Gym's grading system - hides grade associations for v-scale/font */
  gradingSystem?: 'v-scale' | 'font' | 'color-only';
  /** Callback to move or copy a color to the other color list (hold <-> tag) */
  onTransferColor?: (color: ColorGrade, action: 'copy' | 'move') => void;
  /** Label for the target color list (e.g., "Tag Colors" or "Hold Colors") */
  targetLabel?: string;
}

/**
 * Form state for editing a color grade
 */
interface ColorGradeForm {
  color: string;
  displayName: string;
  associationType: GradeAssociationType;
  exactGrade: VGrade | undefined;
  minGrade: VGrade | undefined;
  maxGrade: VGrade | undefined;
}

const createEmptyForm = (color: string = '#3b82f6'): ColorGradeForm => ({
  color,
  displayName: '',
  associationType: 'none',
  exactGrade: undefined,
  minGrade: undefined,
  maxGrade: undefined,
});

const colorGradeToForm = (grade: ColorGrade): ColorGradeForm => ({
  color: grade.color,
  displayName: grade.displayName,
  associationType: grade.associationType,
  exactGrade: grade.associationType === 'exact' ? grade.exactGrade : undefined,
  minGrade: grade.associationType === 'range' ? grade.minGrade : undefined,
  maxGrade: grade.associationType === 'range' ? grade.maxGrade : undefined,
});

const formToColorGrade = (form: ColorGradeForm): ColorGrade => {
  const base = { color: form.color, displayName: form.displayName.trim() };
  if (form.associationType === 'exact' && form.exactGrade) {
    return { ...base, associationType: 'exact', exactGrade: form.exactGrade };
  }
  if (form.associationType === 'range' && form.minGrade && form.maxGrade) {
    return { ...base, associationType: 'range', minGrade: form.minGrade, maxGrade: form.maxGrade };
  }
  return { ...base, associationType: 'none' };
};

/**
 * ColorGradesManager allows editing of gym color grades
 *
 * Features:
 * - Add new color grades with full color picker
 * - Configure grade associations (none, exact, range)
 * - Edit existing grades
 * - Remove grades (with warning if routes use it)
 * - Cannot remove the last grade
 */
export const ColorGradesManager = ({
  colorGrades,
  onSave,
  gymId,
  gradingSystem = 'v-scale',
  onTransferColor,
  targetLabel,
}: ColorGradesManagerProps) => {
  const [grades, setGrades] = useState<ColorGrade[]>(colorGrades);
  // Track color renames: oldName -> newName
  const [pendingRenames, setPendingRenames] = useState<ColorRenameMap>(new Map());
  // Track the original displayName when editing starts
  const [editingOriginalName, setEditingOriginalName] = useState<string | null>(null);

  // Sync local state when parent props change (e.g., after a transfer + refetch)
  useEffect(() => {
    setGrades(colorGrades);
    // Clear pending renames when parent data changes (e.g., after successful save)
    setPendingRenames(new Map());
  }, [colorGrades]);

  // Only show grade associations for color-only grading system
  const showGradeAssociations = gradingSystem === 'color-only';
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<ColorGradeForm>(createEmptyForm());
  const [isAdding, setIsAdding] = useState(false);
  const [addForm, setAddForm] = useState<ColorGradeForm>(createEmptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);
  const [routeCount, setRouteCount] = useState<number | null>(null);
  const [transferMenuIndex, setTransferMenuIndex] = useState<number | null>(null);

  /**
   * Check if there are unsaved changes
   */
  const hasChanges = JSON.stringify(grades) !== JSON.stringify(colorGrades);

  /**
   * Warn user before leaving page with unsaved changes
   */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        // Modern browsers ignore custom messages, but this triggers the warning
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  /**
   * Check how many routes use a specific color grade
   */
  const checkRouteUsage = async (displayName: string): Promise<number> => {
    const db = getFirebaseDb();
    const routesRef = collection(db, 'routes');
    const routesQuery = query(
      routesRef,
      where('gymId', '==', gymId),
      where('colorGrade', '==', displayName)
    );
    const snapshot = await getDocs(routesQuery);
    return snapshot.size;
  };

  /**
   * Start editing a color grade
   */
  const handleEditClick = (index: number) => {
    const grade = grades[index];
    if (!grade) return;

    setEditingIndex(index);
    setEditForm(colorGradeToForm(grade));
    setEditingOriginalName(grade.displayName); // Track original name for rename detection
    setIsAdding(false);
  };

  /**
   * Save edits to a color grade
   */
  const handleEditSave = () => {
    if (editingIndex === null) return;
    if (!editForm.displayName.trim()) {
      setError('Display name is required');
      return;
    }

    const newName = editForm.displayName.trim();

    // Track rename if displayName changed
    if (editingOriginalName && editingOriginalName !== newName) {
      setPendingRenames((prev) => {
        const updated = new Map(prev);
        // Check if this color was already renamed (chain renames: A -> B -> C means A -> C)
        // Find any existing rename that points to the original name
        for (const [oldName, existingNewName] of updated.entries()) {
          if (existingNewName === editingOriginalName) {
            // Update the chain: oldName now points to newName
            updated.set(oldName, newName);
            updated.delete(editingOriginalName); // Remove intermediate
            return updated;
          }
        }
        // No chain, just add the rename
        updated.set(editingOriginalName, newName);
        return updated;
      });
    }

    setGrades((prev) => prev.map((g, i) => (i === editingIndex ? formToColorGrade(editForm) : g)));
    setEditingIndex(null);
    setEditingOriginalName(null);
    setError(null);
    setSuccess(false);
  };

  /**
   * Cancel editing
   */
  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditingOriginalName(null);
  };

  /**
   * Start adding a new color grade
   */
  const handleStartAdd = () => {
    setIsAdding(true);
    setAddForm(createEmptyForm());
    setEditingIndex(null);
  };

  /**
   * Add a new color grade
   */
  const handleAddSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!addForm.displayName.trim()) {
      setError('Display name is required');
      return;
    }

    // Check for duplicate names
    if (
      grades.some((g) => g.displayName.toLowerCase() === addForm.displayName.trim().toLowerCase())
    ) {
      setError('A color grade with this name already exists');
      return;
    }

    setGrades((prev) => [...prev, formToColorGrade(addForm)]);
    setIsAdding(false);
    setAddForm(createEmptyForm());
    setError(null);
    setSuccess(false);
  };

  /**
   * Cancel adding
   */
  const handleAddCancel = () => {
    setIsAdding(false);
    setError(null);
  };

  /**
   * Initiate removal of a color grade
   */
  const handleRemoveClick = async (index: number) => {
    // Cannot remove the last grade
    if (grades.length <= 1) {
      setError('Cannot remove the last color grade. Gyms must have at least one.');
      return;
    }

    const grade = grades[index];
    if (!grade) return;

    // Check if routes are using this grade
    try {
      const count = await checkRouteUsage(grade.displayName);
      setRouteCount(count);
      setRemovingIndex(index);
    } catch (err) {
      console.error('Error checking route usage:', err);
      setRemovingIndex(index);
      setRouteCount(null);
    }
  };

  /**
   * Confirm and execute removal
   */
  const handleConfirmRemove = () => {
    if (removingIndex === null) return;

    setGrades((prev) => prev.filter((_, i) => i !== removingIndex));
    setRemovingIndex(null);
    setRouteCount(null);
    setSuccess(false);
  };

  /**
   * Cancel removal
   */
  const handleCancelRemove = () => {
    setRemovingIndex(null);
    setRouteCount(null);
  };

  /**
   * Save all changes to Firestore
   * Sorts grades by their grade association before saving
   * Passes any color renames to update existing routes
   */
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Sort grades by grade association before saving
      const sortedGrades = sortColorGradesByGrade(grades);
      setGrades(sortedGrades);
      // Pass renames so parent can update routes that use old color names
      await onSave(sortedGrades, pendingRenames.size > 0 ? pendingRenames : undefined);
      setPendingRenames(new Map()); // Clear renames after successful save
      setSuccess(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save changes';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Format grade association for display
   */
  const formatGradeAssociation = (grade: ColorGrade): string | null => {
    if (grade.associationType === 'exact' && grade.exactGrade) {
      return grade.exactGrade;
    }
    if (grade.associationType === 'range' && grade.minGrade && grade.maxGrade) {
      return `${grade.minGrade} - ${grade.maxGrade}`;
    }
    // Fallback for legacy data
    if (grade.suggestedVGrade) {
      return grade.suggestedVGrade;
    }
    return null;
  };

  return (
    <div style={containerStyle}>
      {/* Current Grades List */}
      <div style={listContainerStyle}>
        {grades.map((grade, index) => (
          <div key={`${grade.displayName}-${index}`} style={gradeItemStyle}>
            {editingIndex === index ? (
              /* Edit Mode */
              <div style={editFormStyle}>
                <ColorPicker
                  value={editForm.color}
                  onChange={(color) => setEditForm((f) => ({ ...f, color }))}
                  label="Color"
                />

                <div style={nameInputGroupStyle}>
                  <label style={smallLabelStyle}>Display Name *</label>
                  <input
                    type="text"
                    value={editForm.displayName}
                    onChange={(e) => setEditForm((f) => ({ ...f, displayName: e.target.value }))}
                    style={inputStyle}
                    placeholder="e.g., Blue Circuit..."
                    maxLength={50}
                  />
                </div>

                {showGradeAssociations && (
                  <div style={associationSectionStyle}>
                    <label style={smallLabelStyle}>Grade Association</label>
                    <GradeAssociationSelector
                      associationType={editForm.associationType}
                      exactGrade={editForm.exactGrade}
                      minGrade={editForm.minGrade}
                      maxGrade={editForm.maxGrade}
                      onAssociationTypeChange={(type) =>
                        setEditForm((f) => ({ ...f, associationType: type }))
                      }
                      onExactGradeChange={(grade) =>
                        setEditForm((f) => ({ ...f, exactGrade: grade }))
                      }
                      onMinGradeChange={(grade) => setEditForm((f) => ({ ...f, minGrade: grade }))}
                      onMaxGradeChange={(grade) => setEditForm((f) => ({ ...f, maxGrade: grade }))}
                    />
                  </div>
                )}

                <div style={editActionsStyle}>
                  <button type="button" onClick={handleEditCancel} style={cancelEditButtonStyle}>
                    Cancel
                  </button>
                  <button type="button" onClick={handleEditSave} style={saveEditButtonStyle}>
                    Save
                  </button>
                </div>
              </div>
            ) : (
              /* Display Mode */
              <>
                <div style={gradeInfoStyle}>
                  <div
                    style={{
                      ...colorSwatchStyle,
                      backgroundColor: grade.color,
                      border:
                        grade.color.toLowerCase() === '#ffffff'
                          ? '1px solid var(--color-border)'
                          : 'none',
                    }}
                  />
                  <span style={gradeNameStyle}>{grade.displayName}</span>
                  {showGradeAssociations && formatGradeAssociation(grade) && (
                    <span style={gradeAssociationStyle}>{formatGradeAssociation(grade)}</span>
                  )}
                </div>

                <div style={actionsStyle}>
                  <button
                    type="button"
                    onClick={() => handleEditClick(index)}
                    style={editButtonStyle}
                  >
                    Edit
                  </button>
                  {onTransferColor && targetLabel && (
                    <div style={transferContainerStyle}>
                      <button
                        type="button"
                        onClick={() =>
                          setTransferMenuIndex(transferMenuIndex === index ? null : index)
                        }
                        style={transferButtonStyle}
                        title={`Copy or move to ${targetLabel}`}
                      >
                        &rarr;
                      </button>
                      {transferMenuIndex === index && (
                        <div style={transferMenuStyle}>
                          <button
                            type="button"
                            onClick={() => {
                              onTransferColor(grade, 'copy');
                              setTransferMenuIndex(null);
                            }}
                            style={transferMenuItemStyle}
                          >
                            Copy to {targetLabel}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              onTransferColor(grade, 'move');
                              setTransferMenuIndex(null);
                            }}
                            style={transferMenuItemStyle}
                          >
                            Move to {targetLabel}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveClick(index)}
                    disabled={grades.length <= 1}
                    style={{
                      ...removeButtonStyle,
                      ...(grades.length <= 1 ? disabledRemoveButtonStyle : {}),
                    }}
                  >
                    Remove
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Add New Grade Form */}
      {isAdding ? (
        <form onSubmit={handleAddSubmit} style={addFormContainerStyle}>
          <ColorPicker
            value={addForm.color}
            onChange={(color) => setAddForm((f) => ({ ...f, color }))}
            label="Color"
          />

          <div style={nameInputGroupStyle}>
            <label style={smallLabelStyle}>Display Name *</label>
            <input
              type="text"
              value={addForm.displayName}
              onChange={(e) => setAddForm((f) => ({ ...f, displayName: e.target.value }))}
              style={inputStyle}
              placeholder="e.g., Beginner, Red Circuit..."
              maxLength={50}
              required
            />
          </div>

          {showGradeAssociations && (
            <div style={associationSectionStyle}>
              <label style={smallLabelStyle}>Grade Association</label>
              <GradeAssociationSelector
                associationType={addForm.associationType}
                exactGrade={addForm.exactGrade}
                minGrade={addForm.minGrade}
                maxGrade={addForm.maxGrade}
                onAssociationTypeChange={(type) =>
                  setAddForm((f) => ({ ...f, associationType: type }))
                }
                onExactGradeChange={(grade) => setAddForm((f) => ({ ...f, exactGrade: grade }))}
                onMinGradeChange={(grade) => setAddForm((f) => ({ ...f, minGrade: grade }))}
                onMaxGradeChange={(grade) => setAddForm((f) => ({ ...f, maxGrade: grade }))}
              />
            </div>
          )}

          <div style={addFormActionsStyle}>
            <button type="button" onClick={handleAddCancel} style={cancelEditButtonStyle}>
              Cancel
            </button>
            <button type="submit" style={saveEditButtonStyle}>
              Add Color Grade
            </button>
          </div>
        </form>
      ) : (
        <button type="button" onClick={handleStartAdd} style={addButtonStyle}>
          + Add Color Grade
        </button>
      )}

      {/* Error Message */}
      {error && <div style={errorStyle}>{error}</div>}

      {/* Success Message */}
      {success && <div style={successStyle}>Color grades saved successfully!</div>}

      {/* Sticky Save Bar - Always visible when there are unsaved changes */}
      {hasChanges && (
        <div style={fixedBarStyle}>
          <div style={unsavedIndicatorStyle}>
            <span style={unsavedDotStyle} />
            <span>You have unsaved changes</span>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              ...stickySaveButtonStyle,
              ...(saving ? disabledSaveButtonStyle : {}),
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* Removal Confirmation Dialog */}
      {removingIndex !== null && grades[removingIndex] && (
        <div style={overlayStyle}>
          <div style={dialogStyle}>
            <h3 style={dialogTitleStyle}>Remove Color Grade</h3>
            <p style={dialogTextStyle}>
              Are you sure you want to remove <strong>{grades[removingIndex]?.displayName}</strong>?
            </p>
            {routeCount !== null && routeCount > 0 && (
              <p style={warningTextStyle}>
                Warning: {routeCount} route{routeCount !== 1 ? 's' : ''} currently use this color
                grade. These routes will need to be reassigned.
              </p>
            )}
            <div style={dialogActionsStyle}>
              <button type="button" onClick={handleCancelRemove} style={cancelButtonStyle}>
                Cancel
              </button>
              <button type="button" onClick={handleConfirmRemove} style={confirmRemoveButtonStyle}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Component styles using CSS variables for dark mode support
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const listContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const gradeItemStyle: CSSProperties = {
  padding: '16px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
};

const gradeInfoStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flex: 1,
};

const colorSwatchStyle: CSSProperties = {
  width: '32px',
  height: '32px',
  borderRadius: '6px',
  flexShrink: 0,
  // Subtle border visible in both light and dark modes
  border: '2px solid var(--color-border)',
};

const gradeNameStyle: CSSProperties = {
  fontSize: '0.9375rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
};

const gradeAssociationStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  padding: '4px 10px',
  borderRadius: '4px',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  marginTop: '12px',
};

const editButtonStyle: CSSProperties = {
  padding: '6px 12px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: 'none',
  borderRadius: '4px',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};

const transferContainerStyle: CSSProperties = {
  position: 'relative',
};

const transferButtonStyle: CSSProperties = {
  padding: '6px 10px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  lineHeight: 1,
};

const transferMenuStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  right: 0,
  marginTop: '4px',
  backgroundColor: 'var(--color-bg-primary)',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  zIndex: 10,
  minWidth: '180px',
  overflow: 'hidden',
};

const transferMenuItemStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '10px 14px',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  backgroundColor: 'transparent',
  border: 'none',
  borderBottom: '1px solid var(--color-border)',
  cursor: 'pointer',
  textAlign: 'left',
};

const removeButtonStyle: CSSProperties = {
  padding: '6px 12px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  border: `1px solid var(--color-error)`,
  borderRadius: '4px',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--color-error)',
  cursor: 'pointer',
};

const disabledRemoveButtonStyle: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

const editFormStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const nameInputGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const associationSectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px',
  backgroundColor: 'var(--color-bg-tertiary)',
  borderRadius: '8px',
};

const smallLabelStyle: CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
};

const inputStyle: CSSProperties = {
  padding: '10px 14px',
  fontSize: '0.9375rem',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  backgroundColor: 'var(--color-bg-secondary)',
  color: 'var(--color-text-primary)',
};

const editActionsStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  justifyContent: 'flex-end',
};

const cancelEditButtonStyle: CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const saveEditButtonStyle: CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const addFormContainerStyle: CSSProperties = {
  padding: '20px',
  backgroundColor: 'var(--color-badge-info-bg)',
  borderRadius: '8px',
  border: '1px solid var(--color-primary)',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const addFormActionsStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  justifyContent: 'flex-end',
};

const addButtonStyle: CSSProperties = {
  padding: '14px 20px',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-primary)',
  border: '2px dashed var(--color-primary)',
  borderRadius: '8px',
  fontSize: '0.9375rem',
  fontWeight: 500,
  cursor: 'pointer',
  textAlign: 'center',
};

const errorStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-badge-warning-bg)',
  border: `1px solid var(--color-error)`,
  color: 'var(--color-error)',
  fontSize: '0.875rem',
};

const successStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-badge-success-bg)',
  border: `1px solid var(--color-success)`,
  color: 'var(--color-badge-success-text)',
  fontSize: '0.875rem',
};

const fixedBarStyle: CSSProperties = {
  position: 'fixed',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  maxWidth: '560px',
  width: 'calc(100% - 40px)',
  backgroundColor: 'var(--color-badge-warning-bg)',
  border: '2px solid var(--color-warning, #f59e0b)',
  borderRadius: '12px',
  padding: '16px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
  zIndex: 100,
};

const unsavedIndicatorStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  fontSize: '0.9375rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
};

const unsavedDotStyle: CSSProperties = {
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: 'var(--color-warning, #f59e0b)',
  animation: 'pulse 2s ease-in-out infinite',
};

const stickySaveButtonStyle: CSSProperties = {
  padding: '12px 24px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
  flexShrink: 0,
};

const disabledSaveButtonStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-muted)',
  cursor: 'not-allowed',
};

const overlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px',
};

const dialogStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  padding: '24px',
  maxWidth: '400px',
  width: '100%',
};

const dialogTitleStyle: CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: '12px',
};

const dialogTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '12px',
};

const warningTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-error)',
  backgroundColor: 'var(--color-badge-warning-bg)',
  padding: '12px',
  borderRadius: '6px',
  marginBottom: '16px',
};

const dialogActionsStyle: CSSProperties = {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
};

const cancelButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const confirmRemoveButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-error)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

export default ColorGradesManager;
